// src/features/admissions/PatientAdmissionCard.tsx
import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  HStack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useActiveAdmission, useListAdmissions } from "../../apis/patients";
import AdmitPatientModal from "../patients/components/AdmitPatientModal";
import DischargeConfirm from "./DischargeConfirm";

type AdmissionLike = {
  _id: string;
  status: "admitted" | "discharged" | "transferred" | string;
  admittedAt?: string;
  createdAt?: string;
  dischargedAt?: string;
  ward?: string;
  bed?: string;
  patientId?: string;
};

const fmt = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

type Props = {
  patientId: string;
  appointmentId?: string;
  /** Rich seed text built in AppointmentDetails */
  seedText?: string;
  onAdmissionId?: (id?: string | null) => void;
};

export default function PatientAdmissionCard({
  patientId,
  appointmentId,
  seedText,
  onAdmissionId,
}: Props) {
  const navigate = useNavigate();

  const admitDlg = useDisclosure();
  const dischargeDlg = useDisclosure();

  const { data: active, isFetching: isCheckingActive } =
    useActiveAdmission(patientId);

  const { data: listData, isFetching: isLoadingList } = useListAdmissions({
    patientId,
  });

  const latest: AdmissionLike | undefined = Array.isArray(listData)
    ? (listData as AdmissionLike[])[0]
    : undefined;

  const hasActive = !!active && !!active._id && active.status === "admitted";

  // After admission is created, go to its detail page and pass the seed via location.state
  const handleAdmitted = (newAdmissionId?: string | null) => {
    if (!newAdmissionId) return;

    if (onAdmissionId) onAdmissionId(newAdmissionId);

    const trimmedSeed = (seedText || "").trim();

    const navState =
      trimmedSeed.length > 0 ? { initialSeed: trimmedSeed } : undefined;

    navigate(
      `/admissions/${newAdmissionId}`,
      navState ? { state: navState } : undefined
    );
  };

  // ---------- While checking active ----------
  if (isCheckingActive && !hasActive) {
    return (
      <Alert status="info" variant="subtle" borderRadius="md" mb={4}>
        <AlertIcon />
        <Text>Checking admission status…</Text>
      </Alert>
    );
  }

  // ---------- Active admission banner ----------
  if (hasActive && active) {
    const admittedAtISO = active.admittedAt ?? active.createdAt;

    return (
      <>
        <Alert status="success" variant="subtle" borderRadius="md" mb={4}>
          <AlertIcon />
          <HStack justify="space-between" w="100%" align="center">
            <Text>
              <Badge colorScheme="green" mr={2}>
                Admitted
              </Badge>
              {admittedAtISO ? `since ${fmt(admittedAtISO)}` : ""}
              {active.ward ? ` • Ward: ${active.ward}` : ""}
              {active.bed ? ` • Bed: ${active.bed}` : ""}
            </Text>

            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/admissions/${active._id}`)}
              >
                View details
              </Button>
              <Button size="sm" colorScheme="red" onClick={dischargeDlg.onOpen}>
                Discharge
              </Button>
            </HStack>
          </HStack>
        </Alert>

        <DischargeConfirm
          isOpen={dischargeDlg.isOpen}
          onClose={dischargeDlg.onClose}
          admissionId={active._id}
          patientId={patientId}
        />
      </>
    );
  }

  // ---------- No active: maybe recently discharged ----------
  const showDischargedBanner =
    !hasActive &&
    !isCheckingActive &&
    !isLoadingList &&
    latest &&
    latest.status === "discharged";

  if (showDischargedBanner && latest) {
    return (
      <>
        <Alert status="info" variant="subtle" borderRadius="md" mb={4}>
          <AlertIcon />
          <HStack justify="space-between" w="100%" align="center">
            <Text>
              <Badge colorScheme="gray" mr={2}>
                Discharged
              </Badge>
              on <b>{fmt(latest.dischargedAt)}</b>
              {latest.ward ? ` • Ward: ${latest.ward}` : ""}
              {latest.bed ? ` • Bed: ${latest.bed}` : ""}
            </Text>

            <HStack spacing={2}>
              <Button
                as={RouterLink}
                to={`/admissions?patientId=${encodeURIComponent(patientId)}`}
                size="sm"
                variant="ghost"
              >
                View history
              </Button>
              <Button size="sm" onClick={admitDlg.onOpen}>
                Re-admit
              </Button>
            </HStack>
          </HStack>
        </Alert>

        <AdmitPatientModal
          isOpen={admitDlg.isOpen}
          onClose={admitDlg.onClose}
          patientId={patientId}
          appointmentId={appointmentId}
          onAdmitted={handleAdmitted}
        />
      </>
    );
  }

  // ---------- Default: no active admission yet ----------
  return (
    <>
      <Alert status="info" variant="subtle" borderRadius="md" mb={4}>
        <AlertIcon />
        <HStack justify="space-between" w="100%" align="center">
          <Text>No active admission</Text>
          <Button size="sm" onClick={admitDlg.onOpen}>
            Admit patient
          </Button>
        </HStack>
      </Alert>

      <AdmitPatientModal
        isOpen={admitDlg.isOpen}
        onClose={admitDlg.onClose}
        patientId={patientId}
        appointmentId={appointmentId}
        onAdmitted={handleAdmitted}
      />
    </>
  );
}
